import axiosInstance from 'shared/services/api/axiosInstance';
import { getSelectedTeamNumericId } from 'shared/utils/selectedTeamId';

const teamHeaders = () => {
  const id = getSelectedTeamNumericId();
  return id != null ? { 'X-Team-Id': String(id) } : {};
};

export async function listPrompts(filter = {}) {
  const { data } = await axiosInstance.get('/prompts', {
    headers: teamHeaders(),
    params: { tag: filter.tag || undefined, label: filter.label || undefined },
  });
  return data.prompts || [];
}

export async function createPrompt(name, description, tags) {
  const { data } = await axiosInstance.post(
    '/prompts',
    { name, description, tags },
    { headers: teamHeaders() },
  );
  return data.prompt;
}

/**
 * MCP servers this team has connected, each with its last-discovered tools.
 *
 * Schemas are late-bound — a prompt version stores only {credential_id,
 * tool_names}, so this is a live view for the picker, not what a prompt "has".
 */
export async function listMcpServers() {
  const { data } = await axiosInstance.get('/prompts/mcp-servers', { headers: teamHeaders() });
  return data.servers || [];
}

export async function refreshMcpServer(credentialId) {
  const { data } = await axiosInstance.post(
    `/prompts/mcp-servers/${credentialId}/refresh`,
    {},
    { headers: teamHeaders() },
  );
  return data.tools || [];
}

export async function getPrompt(id) {
  const { data } = await axiosInstance.get(`/prompts/${id}`, { headers: teamHeaders() });
  return data.prompt;
}

export async function updatePrompt(id, patch) {
  const { data } = await axiosInstance.patch(`/prompts/${id}`, patch, { headers: teamHeaders() });
  return data.prompt;
}

export async function deletePrompt(id) {
  const { data } = await axiosInstance.delete(`/prompts/${id}`, { headers: teamHeaders() });
  return data;
}

export async function listLabels() {
  const { data } = await axiosInstance.get('/prompts/labels', { headers: teamHeaders() });
  return data.labels || [];
}

export async function addLabel(label) {
  const { data } = await axiosInstance.post('/prompts/labels', { label }, { headers: teamHeaders() });
  return data.labels || [];
}

export async function listVersions(id) {
  const { data } = await axiosInstance.get(`/prompts/${id}/versions`, { headers: teamHeaders() });
  return data.versions || [];
}

export async function createVersion(id, payload) {
  const { data } = await axiosInstance.post(`/prompts/${id}/versions`, payload, { headers: teamHeaders() });
  // unchanged=true means the content matched the latest version (git-style no-op).
  return { ...data.version, unchanged: Boolean(data.unchanged) };
}

export async function setVersionLabel(id, versionNo, label) {
  const { data } = await axiosInstance.post(
    `/prompts/${id}/versions/${versionNo}/label`,
    { label },
    { headers: teamHeaders() },
  );
  return data.version;
}

export async function removeVersionLabel(id, versionNo, label) {
  const { data } = await axiosInstance.delete(
    `/prompts/${id}/versions/${versionNo}/label/${encodeURIComponent(label)}`,
    { headers: teamHeaders() },
  );
  return data.version;
}

// ── per-prompt edit access ────────────────────────────────────────────────────

/** { owner_id, editors: [{ user_id, email, added_by, added_at }] } */
export async function listEditors(id) {
  const { data } = await axiosInstance.get(`/prompts/${id}/editors`, { headers: teamHeaders() });
  return { ownerId: data.owner_id ?? null, editors: data.editors || [] };
}

/** Grant edit access to a team member by userId (or email). Owner-only. */
export async function addEditor(id, { userId, email }) {
  const { data } = await axiosInstance.post(
    `/prompts/${id}/editors`,
    { userId, email },
    { headers: teamHeaders() },
  );
  return data.editors || [];
}

export async function removeEditor(id, userId) {
  const { data } = await axiosInstance.delete(
    `/prompts/${id}/editors/${userId}`,
    { headers: teamHeaders() },
  );
  return data.editors || [];
}

/**
 * Team members (userId + name + email), for the invite picker and for resolving
 * a version's author email → display name. Mirrors the settings Members page.
 */
export async function listTeamMembers() {
  const teamId = getSelectedTeamNumericId();
  if (teamId == null) return [];
  const { data } = await axiosInstance.get(`/team/${teamId}/members`);
  return data?.members || [];
}

/**
 * One VERSION's analytics — summary, time series and the agents calling it.
 *
 * Scoped to a single version_hash on the server, so the request costs the same
 * whether the prompt has two versions or two hundred, and the charts describe
 * exactly the version being viewed rather than a blend of all of them.
 */
export async function getPromptVersionUsage(id, versionHash, { from, to, bucket } = {}) {
  const { data } = await axiosInstance.get(`/prompts/${id}/usage/${versionHash}`, {
    headers: teamHeaders(),
    params: { from, to, bucket },
  });
  return data;
}
